import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { usageApi } from '../api/admin';
import { Button, Card, EmptyState, ErrorBanner, Input, ListSkeleton, Meter, PageHeader, Table, Td, Th, Tr } from '../components';
import { cn } from '../lib/cn';
import { formatMonth, isMonth, monthOf, shiftMonth } from '../lib/dates';
import { formatNumber, formatUsd } from '../lib/format';
import { sumUsage } from '../lib/usage';

export function UsagePage() {
  const [sp, setSp] = useSearchParams();
  const current = monthOf();
  const raw = sp.get('month');
  const month = isMonth(raw) ? raw : current;
  const setMonth = (m: string) => setSp(m === current ? {} : { month: m }, { replace: true });

  const query = useQuery({ queryKey: ['usage', month], queryFn: () => usageApi.get(month), placeholderData: keepPreviousData });
  const exportCsv = useMutation({ mutationFn: () => usageApi.exportCsv(month) });
  const rows = [...(query.data ?? [])].sort((a, b) => b.costUsd - a.costUsd);
  const totals = sumUsage(rows);

  return (
    <div>
      <PageHeader
        title="Usage & costs"
        description={`AI API usage per rep for ${formatMonth(month)}.`}
        actions={
          <>
            <Button size="sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month">
              ←
            </Button>
            <Input type="month" aria-label="Month" value={month} max={current} onChange={(e) => isMonth(e.target.value) && setMonth(e.target.value)} className="w-40" />
            <Button size="sm" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= current} aria-label="Next month">
              →
            </Button>
            <Button onClick={() => exportCsv.mutate()} loading={exportCsv.isPending}>
              Export CSV
            </Button>
          </>
        }
      />
      <ErrorBanner error={exportCsv.error} className="mb-3" />
      <Card bodyClassName="p-0">
        {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} className="m-3" />}
        {query.isLoading ? (
          <ListSkeleton />
        ) : rows.length === 0 && query.isSuccess ? (
          <EmptyState title="No usage this month" />
        ) : rows.length > 0 ? (
          <div className={cn(query.isPlaceholderData && 'opacity-60')}>
            <Table>
              <thead>
                <tr>
                  <Th>Rep</Th>
                  <Th className="text-right">Requests</Th>
                  <Th className="text-right">Input tokens</Th>
                  <Th className="text-right">Output tokens</Th>
                  <Th className="text-right">Cache read</Th>
                  <Th className="text-right">Cache write</Th>
                  <Th className="min-w-56">Cost vs limit</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Tr key={r.userId}>
                    <Td>
                      <div>{r.userName}</div>
                      <div className="text-xs text-muted">{r.email}</div>
                    </Td>
                    <Td className="text-right tabular-nums">{formatNumber(r.requests)}</Td>
                    <Td className="text-right tabular-nums">{formatNumber(r.inputTokens)}</Td>
                    <Td className="text-right tabular-nums">{formatNumber(r.outputTokens)}</Td>
                    <Td className="text-right tabular-nums">{formatNumber(r.cacheReadTokens)}</Td>
                    <Td className="text-right tabular-nums">{formatNumber(r.cacheWriteTokens)}</Td>
                    <Td>
                      <div className="mb-1 flex justify-between gap-2 text-xs tabular-nums">
                        <span className="font-medium text-ink">{formatUsd(r.costUsd)}</span>
                        <span className="text-muted">
                          of {formatUsd(r.limitUsd)}
                          {r.limitUsd > 0 && r.costUsd >= r.limitUsd && <span className="ml-1 font-medium text-danger">⚠ limit reached</span>}
                        </span>
                      </div>
                      <Meter value={r.costUsd} max={r.limitUsd} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-subtle font-medium">
                  <Td>Total</Td>
                  <Td className="text-right tabular-nums">{formatNumber(totals.requests)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(totals.inputTokens)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(totals.outputTokens)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(totals.cacheReadTokens)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(totals.cacheWriteTokens)}</Td>
                  <Td className="tabular-nums">
                    {formatUsd(totals.costUsd)} <span className="font-normal text-muted">of {formatUsd(totals.limitUsd)} combined limits</span>
                  </Td>
                </tr>
              </tfoot>
            </Table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
